import React, { HTMLAttributes } from 'react'

import classNames from 'classnames'

import { FeedCheckIcon, CommonFollowingCircleIcon } from '@local/assets/icons'
import { ProfileDefaultImage } from '@local/assets/images'

import { Decoration } from '@web/gql/graphql'
import { getPhotoInfraUrlWithType } from '@web/libs/media/thumbnail/thumbnail'
import { ProfileThumbnailTypes, PartialPhotoInfraThumbnail } from '@web/libs/media/thumbnail/thumbnail.constant'
import { reactionTypeMap } from '@web/types/reaction-type'

import styles from './ProfileThumbnail.module.scss'

export const SMALL_PROFILE_THUMBNAILS: ProfileThumbnailTypes = {
  contact: 'f102_102',
  myplace: 'f120',
}

export const PROFILE_THUMBNAILS: ProfileThumbnailTypes = {
  contact: 'f210_210',
  myplace: 'f220_220_gif',
}

interface ProfileBorderProps {
  border?: Pick<Decoration, 'imageUrl' | 'name'> | null
  outPosition?: number
}
const ProfileBorder = ({ border, outPosition = 7 }: ProfileBorderProps) => {
  if (!border?.imageUrl) return null

  const outPositionValue = outPosition * -1

  return (
    <div
      className={styles.decorate}
      style={{ top: outPositionValue, right: outPositionValue, bottom: outPositionValue, left: outPositionValue }}
    >
      <img
        className={styles.profile_border}
        src={getPhotoInfraUrlWithType(border.imageUrl, { myplace: 'f220_220_70_sharpen' })}
        width="100%"
        height="100%"
        alt="프로필 띠"
      />
    </div>
  )
}

interface Props extends HTMLAttributes<HTMLDivElement> {
  size?: number
  outPosition?: number
  isFollowing?: boolean | null
  forceLightMode?: boolean
  thumbnailUrl?: string | null
  thumbnailTypes?: PartialPhotoInfraThumbnail
  isTopHome?: boolean
  followingAnimationActivation?: boolean
  noBorder?: boolean
  isSmall?: boolean
  hasShadow?: boolean
  border?: Pick<Decoration, 'imageUrl' | 'name'> | null
  reactionType?: string
}
const ProfileThumbnail: React.FC<Props> = ({
  size = 38,
  outPosition,
  isFollowing = false,
  forceLightMode = false,
  thumbnailUrl,
  thumbnailTypes,
  isTopHome = false,
  followingAnimationActivation = false,
  noBorder = false,
  isSmall = false,
  hasShadow = true,
  border,
  reactionType,
  ...props
}) => {
  const typeAnimated = isTopHome && isFollowing && followingAnimationActivation
  const thumbSize = noBorder ? size : size + 4
  const defaultThumbnailTypes = isSmall || size < 30 ? SMALL_PROFILE_THUMBNAILS : PROFILE_THUMBNAILS

  return (
    <div
      className={classNames(styles.author_thumb_area, {
        [styles.type_following]: isFollowing,
        [styles.type_force_lightcolor]: forceLightMode,
        [styles.type_animated]: typeAnimated,
        [styles.type_no_border]: noBorder,
        [styles.type_top_home]: isTopHome,
        [styles.type_small]: isSmall,
        [styles.type_shadow]: hasShadow,
      })}
      style={{
        width: isTopHome && isFollowing ? size + 8 : thumbSize,
        height: isTopHome && isFollowing ? size + 8 : thumbSize,
      }}
    >
      <div className="place_thumb" {...props}>
        {isFollowing && isTopHome && (
          <>
            <CommonFollowingCircleIcon className={styles.icon_circle} aria-hidden="true" />
            <div className={styles.circle_graph_area_for_ie}>
              <div className={classNames(styles.circle_graph)}>
                <div className={styles.circle} />
              </div>
              <div className={classNames(styles.circle_graph, styles.type_full)}>
                <div className={styles.circle} />
              </div>
            </div>
          </>
        )}
        {thumbnailUrl ? (
          <div
            className={styles.img}
            role="img"
            style={{
              backgroundImage: `url(${getPhotoInfraUrlWithType(
                thumbnailUrl,
                thumbnailTypes ?? defaultThumbnailTypes,
              )})`,
            }}
          >
            <span className="place_blind">프로필</span>
          </div>
        ) : (
          <>
            <img src={ProfileDefaultImage.src} className={styles.img} width="100%" height="100%" alt="프로필" />
          </>
        )}
      </div>
      {isFollowing && !isSmall && !reactionType && (
        <span className={styles.label}>
          <FeedCheckIcon className={styles.icon} aria-hidden="true" />
          <span className="place_blind">팔로잉한 리뷰어</span>
        </span>
      )}
      {reactionType && reactionTypeMap.get(reactionType) && (
        <span className={classNames(styles.label, styles.type_reaction)}>
          <img
            className={styles.emoji}
            src={reactionTypeMap.get(reactionType)?.emojiUrl}
            width="18"
            height="18"
            alt={reactionTypeMap.get(reactionType)?.label.ko}
          />
        </span>
      )}
      <ProfileBorder border={border} outPosition={outPosition} />
    </div>
  )
}

export default ProfileThumbnail
